// AppRouter.jsx

import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    BrowserRouter as Router,
    Route,
    Routes,
    Navigate,
    useLocation,
} from "react-router-dom";

import { lightTheme } from "../theme";
import {
    Box,
    Container,
    Stack,
    ThemeProvider,
    Typography,
} from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";

import LoginScreen from "../pages/LoginScreen";
import NotFoundScreen from "../pages/NotFoundScreen";
import UnAuthorizedScreen from "../pages/UnAuthorizedScreen";

import AppScreen from "../pages/AppScreen";
import InsightsAssistScreen from "../pages/InsightsAssistScreen";
import SettingsScreen from "../pages/SettingsScreen";
import ProfileScreen from "../pages/ProfileScreen";
import DailyReportScreen from "../pages/DailyReportScreen";
import ReleaseScreen from "../pages/ReleaseScreen";
import HotelIdentifierScreen from "../pages/HotelIdentifierScreen";
import TargetsScreen from "../pages/TargetsScreen";
import IconsScreen from "../pages/IconsScreen";
import RedAlertsScreen from "../pages/RedAlertsScreen";
import EventsScreen from "../pages/EventsScreen";
import PDFExportScreen from "../pages/PDFExportScreen";
import IndicatorsScreen from "../pages/IndicatorsScreen";
import EmailScreen from "../pages/EmailScreen";
import TestScreen from "../pages/TestScreen";
import EventsImpactScreen from "../pages/EventsImpactScreen";
import EmailLogsScreen from "../pages/EmailLogsScreen";
import Indicators2Screen from "../pages/Indicators2Screen";

import { loadUserData } from "../actions/auth";
import { setHotelID } from "../actions/hotelID";
import { roles } from "../actions/authRoles";

import NotificationNewVersion from "../components/NotificationNewVersion";
import Track from "../components/Track";
import IsJWTLive from "../components/IsJWTLive";
import Menu from "../components/shared/Menu";
import ProgressBarSteps from "../components/shared/ProgressBarSteps";
import InactivityDetector from "../components/InactivityDetector";
import CheckHide from "../components/shared/CheckHide";
// import DataLoader from "../components/DataLoader";
import MarketingStrategy from "../components/MarketingStrategy";

import { AROLOGO, ARO_ADMIN, ARO_ALL, HOTELS_INFO } from "../configuration";
import Colours from "../helpers/colours";
import ReservationsTableScreen from "../pages/ReservationsTableScreen";
// import PacingScreen from "../pages/PacingScreen";
import UsersActivityScreen from "../pages/UsersActivityScreen";
import ExtrasScreen from "../pages/ExtrasScreen";
import HotelDetailsScreen from "../pages/HotelDetailsScreen";
import Footer from "../components/Footer";
import PickupScreen from "../pages/PickupScreen";
import ForecastScreen from "../pages/ForecastScreen";
import EventsPerformanceScreen from "../pages/EventsPerformanceScreen";
import ManualTargetsScreen from "../pages/ManualTargetsScreen";
import AroInsightsScreen from "./AroInsightsScreen";
import BookedSellingScreen from "../pages/BookedSellingScreen";
import { useGetHotelsQuery } from "../features/hotels/hotelsApi";
import GoogleAdsScreen from "../pages/GoogleAdsScreen";

const DEFAULT_ROUTE = "/app";
const LOGIN_ROUTE = "/login";

const PUBLIC_ROUTES = [
    { path: "/daily/:dailyReportId", element: DailyReportScreen },
    {
        path: "/pdfexport/:reportId",
        element: PDFExportScreen,
        menu: false,
    },
];
const NOT_LOGGED_ROUTES = [
    { path: "/", element: LoginScreen },
    { path: LOGIN_ROUTE, element: LoginScreen },
];
const PRIVATE_ROUTES = [
    { path: "/targets", element: ManualTargetsScreen },
    { path: "/app", element: AppScreen },
    { path: "/app/*", element: AppScreen },
    { path: "/daily", element: DailyReportScreen },
    { path: "/insightassist", element: InsightsAssistScreen },
    { path: "/settings", element: SettingsScreen },
    { path: "/user", element: ProfileScreen },
    { path: "/pdfexport", element: PDFExportScreen, menu: false },
    { path: "/indicators/*", element: IndicatorsScreen },
    { path: "/indicators2", element: Indicators2Screen },
    { path: "/redalerts", element: RedAlertsScreen },
    { path: "/release", element: ReleaseScreen },
    { path: "/targets", element: TargetsScreen },
    { path: "/savedresources", element: MarketingStrategy },
    { path: "/eventsimpact", element: EventsImpactScreen },
    { path: "/eventsPerformance", element: EventsPerformanceScreen },
    { path: "/events/:mode", element: EventsScreen },
    { path: "/lostrevenue", element: AroInsightsScreen },

    // Aró access
    {
        path: "/hotels",
        element: HotelIdentifierScreen,
        roles: ARO_ALL,
    },
    { path: "/ai/pickup", element: PickupScreen, roles: ARO_ALL },
    { path: "/ai/forecast", element: ForecastScreen, roles: ARO_ALL },
    { path: "/icons", element: IconsScreen, roles: ARO_ALL },
    { path: "/email", element: EmailScreen, roles: ARO_ALL },
    { path: "/logs", element: EmailLogsScreen, roles: ARO_ALL },
    {
        path: "/reservations",
        element: ReservationsTableScreen,
        roles: ARO_ALL,
    },
    {
        path: "/usersactivity",
        element: UsersActivityScreen,
        roles: ARO_ALL,
    },
    { path: "/test", element: TestScreen, roles: ARO_ADMIN },
    { path: "/hotel/profile", element: HotelDetailsScreen, roles: ARO_ALL },
    {
        path: "/app/bookedSelling/*",
        element: BookedSellingScreen,
        roles: ARO_ALL,
    },
    { path: "/googleads", element: GoogleAdsScreen, roles: ARO_ALL },
    // { path: "/hotel/targets", element: HotelDetailsScreen, roles: ARO_ALL },
    // { path: "/hotel/email/list", element: HotelDetailsScreen, roles: ARO_ALL },
];

const DelayedNavigate = ({ log, to }) => {
    const [navigate, setNavigate] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const timer = setTimeout(() => {
            if (!log) {
                setNavigate(true);
            }
        }, 3000);

        return () => clearTimeout(timer);
    }, [log, to]);

    if (navigate) {
        const redirectUrl =
            location.pathname !== "/login" && location.pathname !== "/login/"
                ? `?redirect=${btoa(location.pathname + location.search)}`
                : "";

        return <Navigate to={to + redirectUrl} />;
    }

    return (
        <Container>
            <br />
            <br />
            <br />
            <br />
            <Stack
                direction="column"
                justifyContent="center"
                alignItems="center"
                spacing={2}
                sx={{ height: "50vh" }}
            >
                <Typography
                    color={Colours.textColour}
                    letterSpacing={4}
                    fontSize={32}
                >
                    WELCOME TO
                </Typography>
                <img width={"400em"} alt="logo" src={AROLOGO} />
                <br />
                <Box width="50%">
                    <ProgressBarSteps steps={[33, 66, 99, 100]} />
                </Box>
            </Stack>
        </Container>
    );
};

const ProtectedRoute = ({ children, roles = [] }) => {
    const auth = useSelector((state) => state.auth);
    const authRoles = useSelector((state) => state.authRoles.edited ?? []);
    const authHotels = useSelector((state) => state.authRoles.hotels ?? []);
    const location = useLocation();

    useGetHotelsQuery(undefined, {
        skip:
            !auth.user?.jwt ||
            (authRoles.length === 0 && authHotels.length === 0),
    });

    const isAuthorized =
        roles.length === 0 || // If no roles, not need to check
        (authRoles.length > 0 && // if roles, check that we have roles to check
            roles.some((value) => authRoles.includes(value))); // If any role is included in authRoles it is okay

    if (!auth.user) {
        // User is not logged in
        const redirectUrl =
            location.pathname !== "/login" && location.pathname !== "/login/"
                ? `?redirect=${btoa(location.pathname + location.search)}`
                : "";

        return <Navigate to={`/login${redirectUrl}`} />;
    }

    if (!isAuthorized) {
        return <Navigate to="/unauthorized" replace />;
    }

    return children;
};

const ScrollToTop = () => {
    const { pathname } = useLocation();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    return null;
};

// TODO: component that waits a sec if you are not loaded just in case, and if we don't have to token, navigates to default
const AppRouter = () => {
    const [log, setLog] = useState(false);
    const auth = useSelector((state) => state.auth);
    const dispatch = useDispatch();

    useEffect(() => {
        if (auth.user) {
            // We already have a registered user, so we set Logged as true
            setLog(true);
        } else {
            // We don't have a user, so we try to load it from the localStorage
            const user = JSON.parse(window.localStorage.getItem("user"));
            if (user) {
                const {
                    expiresOn,
                    // jwt,
                    // user: email,
                    roles: roles_,
                    rolesHotels,
                } = user;
                if (new Date(expiresOn) > new Date()) {
                    dispatch(loadUserData(user));
                    dispatch(roles(roles_, rolesHotels)); // TODO: Move this to function to avoid repeat code from auth.js
                    // When there are many hotels
                    if (rolesHotels.length > 0) {
                        // When not admin or aro
                        if (rolesHotels.filter((el) => el !== -1).length >= 1) {
                            if (
                                !rolesHotels
                                    .map((h) => String(h))
                                    .includes(
                                        window.localStorage.getItem("hotelID"),
                                    )
                            ) {
                                dispatch(
                                    setHotelID(
                                        String(
                                            rolesHotels.filter(
                                                (el) => el !== -1,
                                            )[0],
                                        ),
                                    ),
                                );
                            }
                        }
                        // When admin or aro
                        else if (rolesHotels[0] === -1) {
                            if (
                                !Object.keys(HOTELS_INFO).includes(
                                    window.localStorage.getItem("hotelID"),
                                )
                            ) {
                                dispatch(setHotelID("default"));
                            }
                        } else {
                        }
                    }
                } else {
                    localStorage.clear();
                }
            }
        }
    }, [auth]);

    return (
        //TODO: make this an array with a map
        <ThemeProvider theme={lightTheme}>
            <NotificationNewVersion />
            <CssBaseline />
            <CheckHide rolesThatCannotSee={ARO_ADMIN}>
                <InactivityDetector />
            </CheckHide>
            <Router>
                <ScrollToTop />
                <Track />
                <IsJWTLive />
                <Routes>
                    {PUBLIC_ROUTES.map(
                        ({ path, element: Component, args = {} }, i) => (
                            <Route
                                path={path}
                                element={<Component {...args} />}
                                key={i}
                            />
                        ),
                    )}

                    {NOT_LOGGED_ROUTES.map(
                        ({ path, element: Component, args = {} }, i) => (
                            <Route
                                path={path}
                                element={
                                    log ? (
                                        <Navigate to={DEFAULT_ROUTE} />
                                    ) : (
                                        <Component {...args} />
                                    )
                                }
                                key={i}
                            />
                        ),
                    )}
                    {PRIVATE_ROUTES.map(
                        (
                            {
                                path,
                                element: Component,
                                args = {},
                                roles,
                                menu = true,
                            },
                            i,
                        ) => (
                            <Route
                                path={path}
                                element={
                                    log ? (
                                        <ProtectedRoute roles={roles || []}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    minHeight: "100vh",
                                                }}
                                            >
                                                {/**To allow the footer to sit at the bottom wether there is scroll or not */}
                                                <Menu showSideMenu={menu}>
                                                    <Component {...args} />

                                                    <Box
                                                        component="footer"
                                                        sx={{ mt: "auto" }}
                                                    >
                                                        <Footer />
                                                    </Box>
                                                </Menu>
                                            </Box>
                                        </ProtectedRoute>
                                    ) : (
                                        <DelayedNavigate
                                            log={log}
                                            to={LOGIN_ROUTE}
                                        />
                                    )
                                }
                                key={i}
                            />
                        ),
                    )}

                    {/* NOT FOUND */}
                    <Route
                        path="/unauthorized"
                        element={<UnAuthorizedScreen />}
                    />
                    <Route path="*" element={<NotFoundScreen />} />
                </Routes>
            </Router>
        </ThemeProvider>
    );
};

export default AppRouter;
